import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const categories = [
    { id: 'all', label: t('blog.allArticles') },
    { id: 'fitness', label: t('blog.fitness') },
    { id: 'nutrition', label: t('blog.nutrition') },
    { id: 'wellness', label: t('blog.wellness') },
    { id: 'training', label: t('blog.training') }
  ];

  const articles = [
    {
      id: 1,
      category: 'fitness',
      title: t('blog.article1.title'),
      excerpt: t('blog.article1.excerpt'),
      image: 'https://readdy.ai/api/search-image?query=modern-fitness-training-gym-equipment-workout-session-with-professional-athlete-performing-strength-exercises-in-bright-spacious-facility-with-minimalist-design-natural-lighting-and-clean-aesthetic&width=800&height=500&seq=blog1&orientation=landscape',
      date: '2024-01-15',
      readTime: '5 min',
      author: 'Ahmed Hassan'
    },
    {
      id: 2,
      category: 'nutrition',
      title: t('blog.article2.title'),
      excerpt: t('blog.article2.excerpt'),
      image: 'https://readdy.ai/api/search-image?query=healthy-nutrition-meal-prep-fresh-vegetables-fruits-lean-proteins-arranged-beautifully-on-clean-white-surface-with-natural-lighting-minimalist-food-photography-style&width=800&height=500&seq=blog2&orientation=landscape',
      date: '2024-01-12',
      readTime: '7 min',
      author: 'Sara Mohamed'
    },
    {
      id: 3,
      category: 'wellness',
      title: t('blog.article3.title'),
      excerpt: t('blog.article3.excerpt'),
      image: 'https://readdy.ai/api/search-image?query=peaceful-meditation-wellness-scene-person-practicing-mindfulness-in-serene-environment-with-soft-natural-light-minimalist-zen-aesthetic-calming-atmosphere&width=800&height=500&seq=blog3&orientation=landscape',
      date: '2024-01-10',
      readTime: '6 min',
      author: 'Fatima Ali'
    },
    {
      id: 4,
      category: 'training',
      title: t('blog.article4.title'),
      excerpt: t('blog.article4.excerpt'),
      image: 'https://readdy.ai/api/search-image?query=high-intensity-interval-training-hiit-workout-athletic-person-performing-dynamic-exercises-in-modern-gym-with-clean-background-professional-fitness-photography&width=800&height=500&seq=blog4&orientation=landscape',
      date: '2024-01-08',
      readTime: '8 min',
      author: 'Omar Khaled'
    },
    {
      id: 5,
      category: 'fitness',
      title: t('blog.article5.title'),
      excerpt: t('blog.article5.excerpt'),
      image: 'https://readdy.ai/api/search-image?query=home-fitness-workout-setup-person-exercising-in-comfortable-home-environment-with-minimal-equipment-natural-lighting-clean-modern-interior-design&width=800&height=500&seq=blog5&orientation=landscape',
      date: '2024-01-05',
      readTime: '5 min',
      author: 'Layla Ibrahim'
    },
    {
      id: 6,
      category: 'nutrition',
      title: t('blog.article6.title'),
      excerpt: t('blog.article6.excerpt'),
      image: 'https://readdy.ai/api/search-image?query=protein-rich-foods-display-various-healthy-protein-sources-including-lean-meats-fish-eggs-legumes-arranged-artistically-on-clean-surface-with-natural-lighting&width=800&height=500&seq=blog6&orientation=landscape',
      date: '2024-01-03',
      readTime: '6 min',
      author: 'Youssef Ahmed'
    },
    {
      id: 7,
      category: 'wellness',
      title: t('blog.article7.title'),
      excerpt: t('blog.article7.excerpt'),
      image: 'https://readdy.ai/api/search-image?query=peaceful-sleep-bedroom-scene-comfortable-bed-with-soft-lighting-calming-colors-minimalist-design-promoting-rest-and-recovery-serene-atmosphere&width=800&height=500&seq=blog7&orientation=landscape',
      date: '2024-01-01',
      readTime: '7 min',
      author: 'Nour Hassan'
    },
    {
      id: 8,
      category: 'training',
      title: t('blog.article8.title'),
      excerpt: t('blog.article8.excerpt'),
      image: 'https://readdy.ai/api/search-image?query=strength-training-weightlifting-athlete-performing-compound-exercises-with-barbells-and-weights-in-professional-gym-setting-with-clean-background&width=800&height=500&seq=blog8&orientation=landscape',
      date: '2023-12-28',
      readTime: '9 min',
      author: 'Karim Mahmoud'
    },
    {
      id: 9,
      category: 'fitness',
      title: t('blog.article9.title'),
      excerpt: t('blog.article9.excerpt'),
      image: 'https://readdy.ai/api/search-image?query=yoga-flexibility-training-person-performing-yoga-poses-in-bright-studio-with-natural-light-minimalist-peaceful-environment-promoting-balance-and-flexibility&width=800&height=500&seq=blog9&orientation=landscape',
      date: '2023-12-25',
      readTime: '6 min',
      author: 'Mona Saeed'
    }
  ];

  const filteredArticles = selectedCategory === 'all' 
    ? articles 
    : articles.filter(article => article.category === selectedCategory);

  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm' : 'bg-white'}`}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <a href="/" className="flex items-center cursor-pointer">
              <img src="https://5vix.com/logo.png" alt="5vix Logo" className="h-10 w-10" />
            </a>
          </div>
          
          <nav className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
            <a href="/#home" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.home')}</a>
            <a href="/#services" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.services')}</a>
            <a href="/#register" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.register')}</a>
            <a href="/blog" className="text-[#673ab7] font-semibold">{t('nav.blog')}</a>
          </nav>

          <div className="flex items-center gap-4">
            <button 
              onClick={() => changeLanguage(i18n.language === 'en' ? 'ar' : 'en')}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer font-medium"
            >
              {i18n.language === 'en' ? 'ع' : 'EN'}
            </button>
            <a href="/#register" className="bg-[#673ab7] text-white px-6 py-2 rounded-lg hover:bg-[#5e35b1] transition-all whitespace-nowrap cursor-pointer">
              {t('nav.tryFree')}
            </a>
            <button onClick={toggleMobileMenu} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={toggleMobileMenu}></div>
          <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-0 w-64 h-full bg-white shadow-lg p-6`}>
            <button onClick={toggleMobileMenu} className="mb-6 text-gray-700 font-medium cursor-pointer">Close</button>
            <nav className="flex flex-col space-y-4">
              <a href="/#home" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.home')}</a>
              <a href="/#services" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.services')}</a>
              <a href="/#register" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.register')}</a>
              <a href="/blog" className="text-[#673ab7] font-semibold" onClick={toggleMobileMenu}>{t('nav.blog')}</a>
            </nav>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#673ab7]/10 to-[#673ab7]/5 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              {t('blog.title')}
            </h1>
            <p className="text-lg text-gray-700">
              {t('blog.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white sticky top-[72px] z-40 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2 rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-[#673ab7] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article) => (
              <article key={article.id} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group">
                <div className="relative h-64 w-full overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4 rtl:left-auto rtl:right-4">
                    <span className="bg-[#673ab7] text-white px-3 py-1 rounded-full text-sm font-medium">
                      {categories.find(c => c.id === article.category)?.label}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-calendar-line"></i>
                      </div>
                      <span>{article.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-time-line"></i>
                      </div>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-[#673ab7] transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#673ab7]/10 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-[#673ab7]"></i>
                      </div>
                      <span className="text-sm font-medium text-gray-700">{article.author}</span>
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center text-[#673ab7] group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform">
                      <i className="ri-arrow-right-line text-xl"></i>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#673ab7] to-[#5e35b1] rounded-2xl p-8 md:p-12 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-line text-3xl"></i>
              </div>
              <h2 className="text-3xl font-bold mb-4">
                {t('blog.newsletterTitle')}
              </h2>
              <p className="text-white/90 mb-8 text-lg">
                {t('blog.newsletterDesc')}
              </p>
              <form className="max-w-md mx-auto">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    placeholder={t('register.email')}
                    className="flex-1 px-6 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                  />
                  <button className="bg-white text-[#673ab7] px-8 py-3 rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer font-semibold">
                    {t('footer.subscribe')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div>
              <a href="/" className="text-3xl font-bold text-white mb-4 block cursor-pointer">5vix</a>
              <p className="text-gray-400 mb-6">
                {t('footer.description')}
              </p>
              <div className="flex flex-col space-y-3 mb-6">
                <div className="flex items-center text-gray-400">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2">
                    <i className="ri-mail-line"></i>
                  </div>
                  <a href="mailto:info@5vix.com" className="hover:text-white transition-colors">info@5vix.com</a>
                </div>
                <div className="flex items-center text-gray-400">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2">
                    <i className="ri-phone-line"></i>
                  </div>
                  <a href="tel:201044553254" className="hover:text-white transition-colors">01044553254</a>
                </div>
              </div>
              <div className="flex space-x-4 rtl:space-x-reverse">
                <a href="https://www.tiktok.com/@5vixofficial" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-pink-500 transition-colors cursor-pointer">
                  <i className="ri-tiktok-fill text-white text-xl"></i>
                </a>
                <a href="https://www.youtube.com/@5vix-c3p" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-red-600 transition-colors cursor-pointer">
                  <i className="ri-youtube-fill text-white text-xl"></i>
                </a>
                <a href="https://www.instagram.com/5vixofficial/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-pink-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-white text-xl"></i>
                </a>
                <a href="https://www.linkedin.com/in/5vix-software-12058b363/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.quickLinks')}</h3>
              <ul className="space-y-3">
                <li><a href="/#home" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.home')}</a></li>
                <li><a href="/#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.services')}</a></li>
                <li><a href="/#register" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.register')}</a></li>
                <li><a href="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.blog')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.support')}</h3>
              <ul className="space-y-3">
                <li><a href="/help-center" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.helpCenter')}</a></li>
                <li><a href="/help-center" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.faq')}</a></li>
                <li><a href="tel:201044553254" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.contactUs')}</a></li>
                <li><a href="/terms" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.terms')}</a></li>
                <li><a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.privacy')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.newsletter')}</h3>
              <p className="text-gray-400 mb-4">{t('footer.newsletterDesc')}</p>
              <form className="mb-4">
                <div className="flex">
                  <input
                    type="email"
                    placeholder={t('register.email')}
                    className="bg-gray-800 text-white px-4 py-2 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-[#673ab7] flex-1"
                  />
                  <button className="bg-[#673ab7] text-white px-4 py-2 rounded-r-lg hover:bg-[#5e35b1] transition-all whitespace-nowrap cursor-pointer">
                    {t('footer.subscribe')}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 mb-4 md:mb-0">{t('footer.copyright')}</p>
              <div className="flex space-x-4 rtl:space-x-reverse">
                <a href="/terms" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.terms')}</a>
                <a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.privacy')}</a>
                <a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.cookies')}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
